class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.onApplyFilters = this.onApplyFilters.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const applyButton = this.querySelector('.mobile-facets__apply');
    if (applyButton) applyButton.addEventListener('click', this.onApplyFilters);

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting', '.mobile-facets__sort'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile')?.closest('menu-drawer')?.bindEvents?.();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetSummary = target.querySelector('.mobile-facets__summary');
    const sourceSummary = source.querySelector('.mobile-facets__summary');

    if (targetSummary && sourceSummary) {
      targetSummary.innerHTML = sourceSummary.innerHTML;
    }

    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash() {}

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  closeMobileDrawer() {
    this.closest('facet-drawer')?.close?.();
  }

  onApplyFilters(event) {
    event.preventDefault();
    const form = this.querySelector('#FacetFiltersFormMobile') || this.querySelector('form');
    if (!form) return;
    this.onSubmitForm(this.createSearchParams(form), event);
    this.closeMobileDrawer();
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const form = event.target.closest('form');

    // Drawer filters apply only when the user clicks "Aplicar".
    if (form?.id === 'FacetFiltersFormMobile') return;

    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    const forms = [];

    sortFilterForms.forEach((formEl) => {
      if (formEl.id === 'FacetSortForm' || formEl.id === 'FacetFiltersForm' || formEl.id === 'FacetSortDrawerForm') {
        forms.push(this.createSearchParams(formEl));
      }
    });
    this.onSubmitForm(forms.join('&'), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

function formatMoneyToken(cents, token) {
  const formats = {
    amount: [2, ',', '.'],
    amount_no_decimals: [0, ',', '.'],
    amount_with_comma_separator: [2, '.', ','],
    amount_no_decimals_with_comma_separator: [0, '.', ','],
    amount_with_apostrophe_separator: [2, "'", '.'],
    amount_no_decimals_with_space_separator: [0, ' ', '.'],
    amount_with_space_separator: [2, ' ', ','],
    amount_with_period_and_space_separator: [2, ' ', '.'],
  };
  const [precision, thousands, decimal] = formats[token] || [2, ',', '.'];
  const parts = (Number(cents) / 100).toFixed(precision).split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, () => thousands);
  return precision > 0 ? parts[0] + decimal + parts[1] : parts[0];
}

function centsToParam(cents) {
  return (Math.round(Number(cents)) / 100).toFixed(2);
}

function formatMoneyAmount(cents, format, { symbol = true, decimals = true } = {}) {
  const source = format || '{{amount}}';
  const match = source.match(/\{\{\s*(\w+)\s*\}\}/);
  let token = match ? match[1] : 'amount';
  if (!decimals) {
    const map = {
      amount: 'amount_no_decimals',
      amount_no_decimals: 'amount_no_decimals',
      amount_with_comma_separator: 'amount_no_decimals_with_comma_separator',
      amount_no_decimals_with_comma_separator: 'amount_no_decimals_with_comma_separator',
      amount_with_apostrophe_separator: 'amount_no_decimals',
      amount_with_space_separator: 'amount_no_decimals_with_space_separator',
      amount_no_decimals_with_space_separator: 'amount_no_decimals_with_space_separator',
      amount_with_period_and_space_separator: 'amount_no_decimals_with_space_separator',
    };
    token = map[token] || 'amount_no_decimals';
  }
  const amount = formatMoneyToken(cents, token);
  if (!symbol || !match) return amount;
  return source.replace(match[0], () => amount);
}

class PriceRange extends HTMLElement {
  connectedCallback() {
    this.disconnected = false;
    if (this.initialized) return;
    this.initialized = true;

    if (this.querySelector('.price-slider')) {
      this.initSlider();
      return;
    }

    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  disconnectedCallback() {
    this.disconnected = true;
    if (!this.minRange || !this.onSliderInput) return;
    this.initialized = false;
    [this.minRange, this.maxRange].forEach((range) => {
      range.removeEventListener('input', this.onSliderInput);
      range.removeEventListener('change', this.commitSlider);
    });
    this.slider?.removeEventListener('pointerdown', this.onPointerDown);
    this.slider?.removeEventListener('pointermove', this.onPointerMove);
    this.slider?.removeEventListener('pointerup', this.onPointerUp);
    this.slider?.removeEventListener('pointercancel', this.onPointerUp);
  }

  initSlider() {
    this.minRange = this.querySelector('.price-slider__range--min');
    this.maxRange = this.querySelector('.price-slider__range--max');
    this.minField = this.querySelector('[data-price-bound="min"]');
    this.maxField = this.querySelector('[data-price-bound="max"]');
    this.minLabel = this.querySelector('[data-price-label="min"]');
    this.maxLabel = this.querySelector('[data-price-label="max"]');
    if (!this.minRange || !this.maxRange || !this.minField || !this.maxField) return;

    this.slider = this.querySelector('.price-slider');
    this.track = this.querySelector('.price-slider__track');
    this.onSliderInput = this.onSliderInput.bind(this);
    this.commitSlider = this.commitSlider.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);

    [this.minRange, this.maxRange].forEach((range) => {
      range.addEventListener('input', this.onSliderInput);
      range.addEventListener('change', this.commitSlider);
    });
    this.slider?.addEventListener('pointerdown', this.onPointerDown);

    this.syncLabels();
    this.loadCollectionBounds();
  }

  onPointerDown(event) {
    if (event.button != null && event.button !== 0) return;
    // Native thumb dragging handles itself; only hijack track clicks.
    if (event.target instanceof HTMLInputElement) return;

    event.preventDefault();
    this.pointerDragging = true;
    this.activeRange = this.rangeClosestTo(event);
    this.slider.setPointerCapture(event.pointerId);
    this.moveActive(event);
    this.slider.addEventListener('pointermove', this.onPointerMove);
    this.slider.addEventListener('pointerup', this.onPointerUp);
    this.slider.addEventListener('pointercancel', this.onPointerUp);
  }

  onPointerMove(event) {
    if (!this.pointerDragging) return;
    this.moveActive(event);
  }

  onPointerUp() {
    if (!this.pointerDragging) return;
    this.pointerDragging = false;
    this.slider.removeEventListener('pointermove', this.onPointerMove);
    this.slider.removeEventListener('pointerup', this.onPointerUp);
    this.slider.removeEventListener('pointercancel', this.onPointerUp);
    this.commitActive();
  }

  rangeClosestTo(event) {
    const value = this.valueFromPointer(event);
    const minDistance = Math.abs(value - Number(this.minRange.value));
    const maxDistance = Math.abs(value - Number(this.maxRange.value));
    return minDistance <= maxDistance ? this.minRange : this.maxRange;
  }

  valueFromPointer(event) {
    const rect = (this.track || this.slider).getBoundingClientRect();
    const min = Number(this.minRange.min);
    const max = Number(this.maxRange.max);
    if (!rect.width || max <= min) return min;
    const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    return Math.round(min + ratio * (max - min));
  }

  moveActive(event) {
    const value = this.valueFromPointer(event);
    if (this.activeRange === this.minRange) {
      this.minRange.value = String(Math.min(value, Number(this.maxRange.value)));
    } else {
      this.maxRange.value = String(Math.max(value, Number(this.minRange.value)));
    }
    this.syncLabels();
  }

  onSliderInput(event) {
    event.stopPropagation();
    if (this.pointerDragging) return;
    this.activeRange = event.currentTarget;
    this.clampThumbs(event.currentTarget);
    this.syncLabels();
  }

  commitSlider(event) {
    event.stopPropagation();
    if (this.pointerDragging) return;
    this.activeRange = event.currentTarget;
    this.clampThumbs(event.currentTarget);
    this.commitActive();
  }

  commitActive() {
    const changed = this.activeRange;
    const minBound = Number(this.minRange.min);
    const maxBound = Number(this.maxRange.max);
    const minValue = Number(this.minRange.value);
    const maxValue = Number(this.maxRange.value);

    if (changed === this.minRange) {
      this.writeBound(this.minField, minValue > minBound ? minValue : null);
      this.minField.dispatchEvent(new Event('input', { bubbles: true }));
    } else if (changed === this.maxRange) {
      this.writeBound(this.maxField, maxValue < maxBound ? maxValue : null);
      this.maxField.dispatchEvent(new Event('input', { bubbles: true }));
    }

    this.activeRange = null;
    this.syncLabels();
  }

  writeBound(field, cents) {
    if (cents == null) {
      field.value = '';
      field.removeAttribute('name');
      return;
    }
    field.name = field.dataset.name;
    field.value = centsToParam(cents);
  }

  clampThumbs(changed) {
    const minValue = Number(this.minRange.value);
    const maxValue = Number(this.maxRange.value);
    if (minValue <= maxValue) return;
    if (changed === this.maxRange) this.maxRange.value = String(minValue);
    else this.minRange.value = String(maxValue);
  }

  syncLabels() {
    if (this.minLabel && this.dataset.labelFrom) {
      this.minLabel.textContent = this.dataset.labelFrom.replace(
        '__price__',
        formatMoneyAmount(this.minRange.value, this.dataset.moneyFormat, { symbol: true, decimals: false })
      );
    }
    if (this.maxLabel && this.dataset.labelTo) {
      this.maxLabel.textContent = this.dataset.labelTo.replace(
        '__price__',
        formatMoneyAmount(this.maxRange.value, this.dataset.moneyFormat, { symbol: true, decimals: false })
      );
    }

    this.syncTrack();

    if (this.activeRange) {
      this.minRange.style.zIndex = this.activeRange === this.minRange ? '3' : '2';
      this.maxRange.style.zIndex = this.activeRange === this.maxRange ? '3' : '2';
      return;
    }

    const span = Number(this.maxRange.max) - Number(this.minRange.min) || 1;
    const thumbsAreClose = (Number(this.maxRange.value) - Number(this.minRange.value)) / span < 0.05;
    this.minRange.style.zIndex = thumbsAreClose ? '3' : '2';
    this.maxRange.style.zIndex = '2';
  }

  syncTrack() {
    if (!this.track || !this.minRange || !this.maxRange) return;

    const min = Number(this.minRange.min);
    const max = Number(this.maxRange.max);
    const span = max - min || 1;
    const start = Math.min(100, Math.max(0, ((Number(this.minRange.value) - min) / span) * 100));
    const end = Math.min(100, Math.max(0, ((Number(this.maxRange.value) - min) / span) * 100));

    this.track.style.setProperty('--price-range-start', `${start}%`);
    this.track.style.setProperty('--price-range-end', `${Math.max(start, end)}%`);
  }

  async loadCollectionBounds() {
    const url = this.dataset.collectionUrl;
    const total = Number(this.dataset.productsCount || 0);
    const scanned = Number(this.dataset.scannedCount || 0);
    if (!url || total <= scanned) return;

    const requestId = Symbol();
    this.boundRequest = requestId;

    try {
      const [minResponse, maxResponse] = await Promise.all([
        fetch(`${url}/products.json?limit=1&sort_by=price-ascending`),
        fetch(`${url}/products.json?limit=1&sort_by=price-descending`),
      ]);
      if (!minResponse.ok || !maxResponse.ok || this.disconnected || this.boundRequest !== requestId) return;
      const minData = await minResponse.json();
      const maxData = await maxResponse.json();
      if (this.disconnected || this.boundRequest !== requestId) return;

      const rate = Number(window.Shopify?.currency?.rate || 1) || 1;
      const fetchedMin = this.variantCents(minData.products?.[0], 'min');
      const fetchedMax = this.variantCents(maxData.products?.[0], 'max');
      if (fetchedMin == null || fetchedMax == null) return;

      this.expandBounds(Math.round(fetchedMin * rate), Math.round(fetchedMax * rate));
    } catch (error) {
      return;
    }
  }

  variantCents(product, mode) {
    if (!product?.variants?.length) return null;
    const prices = product.variants.map((variant) => Math.round(parseFloat(variant.price) * 100));
    return mode === 'max' ? Math.max(...prices) : Math.min(...prices);
  }

  expandBounds(minCents, maxCents) {
    const oldMin = Number(this.minRange.min);
    const oldMax = Number(this.maxRange.max);
    const minWasBound = Number(this.minRange.value) <= oldMin && !this.minField.value;
    const maxWasBound = Number(this.maxRange.value) >= oldMax && !this.maxField.value;
    const nextMin = Math.min(oldMin, minCents);
    const nextMax = Math.max(oldMax, maxCents);
    if (!Number.isFinite(nextMin) || !Number.isFinite(nextMax) || nextMax < nextMin) return;

    [this.minRange, this.maxRange].forEach((range) => {
      range.min = String(nextMin);
      range.max = String(nextMax);
    });

    if (minWasBound) this.minRange.value = String(nextMin);
    if (maxWasBound) this.maxRange.value = String(nextMax);
    this.syncLabels();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (!minInput || !maxInput) return;
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
